/**
 * MSAL.js — Delegated auth for Microsoft Graph API
 *
 * Acquires a Graph access token via the user's own consent (no admin needed).
 * First use triggers a popup consent; subsequent calls use silent token renewal.
 *
 * Multi-tenant: uses "organizations" authority so FGF and L30 users each
 * authenticate against their own AAD tenant. Admin consent must be granted
 * in each tenant (see admin consent URLs in project docs).
 */
import { PublicClientApplication, InteractionRequiredAuthError } from "@azure/msal-browser";

const CLIENT_ID = "a250d76e-3624-44d0-86d0-3fbd4beb7519";

const msalConfig = {
  auth: {
    clientId: CLIENT_ID,
    authority: "https://login.microsoftonline.com/organizations",
    redirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false,
  },
};

let msalInstance = null;

async function getMsalInstance() {
  if (!msalInstance) {
    msalInstance = new PublicClientApplication(msalConfig);
    await msalInstance.initialize();
  }
  return msalInstance;
}

const GRAPH_SCOPES = ["Mail.Send", "Mail.ReadWrite"];

/**
 * Acquire a Microsoft Graph access token with Mail.Send + Mail.ReadWrite scopes.
 *
 * - First call: triggers a popup consent dialog (user approves once)
 * - Subsequent calls: silent token refresh (no popup)
 *
 * @returns {Promise<string>} The access token
 */
export async function getGraphToken() {
  const pca = await getMsalInstance();
  const accounts = pca.getAllAccounts();

  if (accounts.length > 0) {
    // Try silent token acquisition first
    try {
      const result = await pca.acquireTokenSilent({
        scopes: GRAPH_SCOPES,
        account: accounts[0],
      });
      return result.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        // Token expired or consent needed — fall through to popup
      } else {
        throw err;
      }
    }
  }

  // Interactive: popup consent (first time, or after token expiry)
  // Use a dedicated blank page as the popup redirect URI so that
  // Azure SWA's built-in auth doesn't intercept the redirect (the
  // catch-all route requires "authenticated" role, but the popup
  // window has no SWA session cookie).
  const result = await pca.acquireTokenPopup({
    scopes: GRAPH_SCOPES,
    redirectUri: `${window.location.origin}/auth-redirect.html`,
  });
  return result.accessToken;
}
